import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay,
  isSameMonth, isToday, startOfMonth, startOfWeek,
} from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate, daysLabel, STATUS_CONFIG } from "@/lib/status";
import { ChevronLeft, ChevronRight, PartyPopper, CalendarDays } from "lucide-react";

const CHIP_CLASSES = {
  overdue: "bg-[hsl(var(--status-overdue)/0.12)] text-[hsl(var(--status-overdue))]",
  due_soon: "bg-[hsl(var(--status-due-soon)/0.14)] text-[hsl(var(--status-due-soon))]",
  upcoming: "bg-[hsl(var(--status-upcoming)/0.12)] text-[hsl(var(--status-upcoming))]",
  renewed: "bg-muted text-muted-foreground",
};

const DOT_CLASSES = {
  overdue: "bg-[hsl(var(--status-overdue))]",
  due_soon: "bg-[hsl(var(--status-due-soon))]",
  upcoming: "bg-[hsl(var(--status-upcoming))]",
  renewed: "bg-muted-foreground/50",
};

const LEGEND = [
  { key: "overdue", label: "Overdue" },
  { key: "due_soon", label: "Due soon" },
  { key: "upcoming", label: "Upcoming" },
  { key: "renewed", label: "Renewed" },
];

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [items, setItems] = useState(null);
  const [holidaysByYear, setHolidaysByYear] = useState({});
  const [showHolidays, setShowHolidays] = useState(() => localStorage.getItem("biipp_show_holidays") !== "false");
  const [dayDialog, setDayDialog] = useState(null); // Date | null

  const holidayCountry = user?.org?.holiday_country;
  const canManage = ["owner", "admin"].includes(user?.role);

  useEffect(() => {
    api.get("/items").then((res) => setItems(res.data.items)).catch(() => setItems([]));
  }, []);

  const year = cursor.getFullYear();
  useEffect(() => {
    if (!holidayCountry || holidaysByYear[year]) return;
    api.get(`/holidays?year=${year}`)
      .then((res) => setHolidaysByYear((h) => ({ ...h, [year]: res.data.holidays })))
      .catch(() => setHolidaysByYear((h) => ({ ...h, [year]: [] })));
  }, [holidayCountry, year, holidaysByYear]);

  const toggleHolidays = (v) => {
    setShowHolidays(v);
    localStorage.setItem("biipp_show_holidays", String(v));
  };

  const itemsByDate = useMemo(() => {
    const m = {};
    (items || []).forEach((i) => {
      (m[i.due_date] = m[i.due_date] || []).push(i);
    });
    return m;
  }, [items]);

  const holidaysByDate = useMemo(() => {
    const m = {};
    Object.values(holidaysByYear).flat().forEach((h) => {
      (m[h.date] = m[h.date] || []).push(h.name);
    });
    return m;
  }, [holidaysByYear]);

  const days = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
  }), [cursor]);

  const loading = items === null;
  const dialogKey = dayDialog ? format(dayDialog, "yyyy-MM-dd") : null;
  const dialogItems = dialogKey ? itemsByDate[dialogKey] || [] : [];
  const dialogHolidays = dialogKey && showHolidays ? holidaysByDate[dialogKey] || [] : [];

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="calendar-title">Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">Every due date at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          {holidayCountry ? (
            <label className="flex items-center gap-2 mr-2 text-sm text-muted-foreground cursor-pointer">
              <Switch checked={showHolidays} onCheckedChange={toggleHolidays} data-testid="calendar-holidays-toggle" />
              <span className="hidden sm:inline">Public holidays</span>
              <PartyPopper className="h-4 w-4 sm:hidden" />
            </label>
          ) : canManage ? (
            <Link to="/settings?tab=organization">
              <Button variant="ghost" size="sm" className="rounded-lg text-muted-foreground" data-testid="calendar-add-holidays-link">
                <PartyPopper className="h-4 w-4 mr-1.5" /> Add public holidays
              </Button>
            </Link>
          ) : null}
          <Button variant="outline" size="icon" className="rounded-lg" aria-label="Previous month"
            onClick={() => setCursor((c) => addMonths(c, -1))} data-testid="calendar-prev-month-button">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="rounded-lg px-3" onClick={() => setCursor(startOfMonth(new Date()))}
            data-testid="calendar-today-button">
            Today
          </Button>
          <Button variant="outline" size="icon" className="rounded-lg" aria-label="Next month"
            onClick={() => setCursor((c) => addMonths(c, 1))} data-testid="calendar-next-month-button">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold tracking-tight tabular-nums" data-testid="calendar-month-title">
          {format(cursor, "MMMM yyyy")}
        </h2>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {LEGEND.map((l) => (
            <span key={l.key} className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${DOT_CLASSES[l.key]}`} /> {l.label}
            </span>
          ))}
          {holidayCountry && showHolidays && (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[hsl(var(--status-ok))]" /> Holiday
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <Skeleton className="mt-4 h-[480px] w-full" />
      ) : (
        <Card className="mt-4 overflow-hidden">
          <div className="grid grid-cols-7 border-b bg-muted/40">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayItems = itemsByDate[key] || [];
              const dayHolidays = showHolidays ? holidaysByDate[key] || [] : [];
              const inMonth = isSameMonth(day, cursor);
              const today = isToday(day);
              return (
                <button key={key} type="button"
                  onClick={() => (dayItems.length || dayHolidays.length) && setDayDialog(day)}
                  className={`relative min-h-[72px] md:min-h-[112px] border-b border-r p-1.5 md:p-2 text-left align-top transition-colors ${
                    inMonth ? "bg-card" : "bg-muted/30"
                  } ${dayItems.length || dayHolidays.length ? "cursor-pointer hover:bg-muted/50" : "cursor-default"}`}
                  data-testid={`calendar-day-${key}`}>
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs tabular-nums ${
                    today ? "bg-primary text-primary-foreground font-semibold" : inMonth ? "text-foreground" : "text-muted-foreground/50"
                  }`}>
                    {format(day, "d")}
                  </span>

                  {/* Desktop chips */}
                  <div className="hidden md:block mt-1 space-y-1">
                    {dayHolidays.slice(0, 1).map((name) => (
                      <div key={name} className="truncate rounded px-1.5 py-0.5 text-[11px] font-medium bg-[hsl(var(--status-ok)/0.12)] text-[hsl(var(--status-ok))]" title={name}>
                        {name}
                      </div>
                    ))}
                    {dayItems.slice(0, dayHolidays.length ? 2 : 3).map((item) => (
                      <div key={item.id} role="link" tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); navigate(`/items/${item.id}`); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate(`/items/${item.id}`); } }}
                        className={`truncate rounded px-1.5 py-0.5 text-[11px] font-medium hover:opacity-80 ${CHIP_CLASSES[item.status]}`}
                        title={item.name} data-testid={`calendar-item-chip-${item.id}`}>
                        {item.name}
                      </div>
                    ))}
                    {(dayItems.length > (dayHolidays.length ? 2 : 3) || dayHolidays.length > 1) && (
                      <div className="text-[11px] text-muted-foreground px-1">
                        +{Math.max(0, dayItems.length - (dayHolidays.length ? 2 : 3)) + Math.max(0, dayHolidays.length - 1)} more
                      </div>
                    )}
                  </div>

                  {/* Mobile dots */}
                  <div className="md:hidden mt-1 flex flex-wrap gap-0.5">
                    {dayHolidays.length > 0 && <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-ok))]" />}
                    {dayItems.slice(0, 4).map((item) => (
                      <span key={item.id} className={`h-1.5 w-1.5 rounded-full ${DOT_CLASSES[item.status]}`} />
                    ))}
                    {dayItems.length > 4 && <span className="text-[9px] text-muted-foreground leading-none">+{dayItems.length - 4}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {!loading && items.length === 0 && (
        <p className="mt-4 text-sm text-muted-foreground text-center">
          Nothing on the calendar yet — <Link to="/dashboard" className="underline underline-offset-4">add your first item</Link> and its due date will show up here.
        </p>
      )}

      <Dialog open={Boolean(dayDialog)} onOpenChange={(o) => !o && setDayDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              {dayDialog ? format(dayDialog, "EEEE, MMM d, yyyy") : ""}
            </DialogTitle>
            {dialogHolidays.length > 0 && (
              <DialogDescription className="flex items-center gap-1.5 text-[hsl(var(--status-ok))]">
                <PartyPopper className="h-3.5 w-3.5" /> {dialogHolidays.join(" · ")}
              </DialogDescription>
            )}
          </DialogHeader>
          {dialogItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">No items due this day.</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="calendar-day-dialog-list">
              {dialogItems.map((item) => (
                <li key={item.id} className="py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-muted/40 rounded-lg px-2 -mx-2"
                  onClick={() => navigate(`/items/${item.id}`)} data-testid={`calendar-dialog-item-${item.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.category_name} · {daysLabel(item)}
                    </p>
                  </div>
                  <StatusBadge status={item.status} />
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
